import { useQuery } from '@tanstack/react-query';
import { lessonApi, type ClassifiedChatResponse } from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';

/**
 * Lesson-wide aggregate of classified chat exchanges (doubts +
 * deep-dives across every conversation).
 *
 * Consumed by:
 * - the Appendix's Doubts tab (read-only Q&A list)
 * - the Extras tab's "Suggested deep-dives" section (per-card
 *   "Make a lesson" action)
 *
 * One TanStack `useQuery` per lesson keeps the network footprint flat
 * - both consumers share the same cache entry so opening the panel
 * triggers at most one round-trip.
 */
export function useClassifiedChat(lessonId: string | undefined) {
  const query = useQuery<ClassifiedChatResponse>({
    queryKey: queryKeys.lessons.classifiedChat(lessonId ?? ''),
    queryFn: () => lessonApi.getClassifiedChat(lessonId as string),
    enabled: !!lessonId,
    // Stale time mirrors the chat history hook - fresh enough to feel
    // live without polling the LLM-classifier output on every panel
    // switch.
    staleTime: 30_000,
  });

  return {
    data: query.data,
    doubts: query.data?.doubts ?? [],
    deepDives: query.data?.deep_dives ?? [],
    totalDoubts: query.data?.total_doubts ?? 0,
    totalDeepDives: query.data?.total_deep_dives ?? 0,
    isLoading: query.isLoading,
    fetchError: query.error,
    refetch: query.refetch,
  };
}
